import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import SpotifyEmbed from "@/components/SpotifyEmbed";
import { LinkifiedText } from "@/components/LinkifiedText";
import { useSlowLoadingFallback } from "@/hooks/useSlowLoadingFallback";
import { supabase } from "@/integrations/supabase/client";
import { getSpotifyItemType, type PlaylistRow } from "@/lib/contentModels";
import { devLogQueryPerf, readLocalQueryCache, writeLocalQueryCache } from "@/lib/localQueryCache";
import { getSpotifyCanonicalUrl, SPOTIFY_PLAYLIST_EMBED_HEIGHT } from "@/lib/spotify";
import { usePersistWindowScroll } from "@/lib/scrollRestore";

const PAGE_SIZE = 6;
const PLAYLISTS_VISIBLE_COUNT_STORAGE_KEY = "playlists-visible-count";

const Playlists = () => {
  usePersistWindowScroll("/playlists");
  const [visibleCount, setVisibleCount] = useState(() => {
    if (typeof window === "undefined") return PAGE_SIZE;
    const raw = window.sessionStorage.getItem(PLAYLISTS_VISIBLE_COUNT_STORAGE_KEY);
    const parsed = raw ? Number(raw) : PAGE_SIZE;
    if (Number.isNaN(parsed)) return PAGE_SIZE;
    return Math.max(PAGE_SIZE, parsed);
  });
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { data: posts, isPending, isFetching, isError } = useQuery({
    queryKey: ["playlists"],
    queryFn: async () => {
      const startedAt = performance.now();
      const table = "playlists" as never;
      const { data, error } = await supabase
        .from(table)
        .select("*")
        .eq("published", true)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as PlaylistRow[];
      devLogQueryPerf("playlists", startedAt, rows.length);
      return rows;
    },
    initialData: () => readLocalQueryCache<PlaylistRow[]>("cache:playlists"),
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (!posts) return;
    writeLocalQueryCache("cache:playlists", posts);
  }, [posts]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(PLAYLISTS_VISIBLE_COUNT_STORAGE_KEY, String(visibleCount));
  }, [visibleCount]);

  const list = posts ?? [];
  const filteredList = useMemo(
    () => list.filter((post) => getSpotifyItemType(post.spotify_url) === "playlist"),
    [list],
  );

  const showLoading = isPending || isFetching;
  const slowLoad = useSlowLoadingFallback(showLoading, isError);
  const visiblePlaylists = filteredList.slice(0, visibleCount);
  const hasMore = visibleCount < filteredList.length;

  // Infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredList.length));
      },
      { rootMargin: "300px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [filteredList.length, visibleCount]);

  const showSkeleton = showLoading && !slowLoad;

  useEffect(() => {
    if (filteredList.length === 0) return;
    setVisibleCount((prev) => Math.min(Math.max(PAGE_SIZE, prev), filteredList.length));
  }, [filteredList.length]);

  return (
    <div className="mx-auto max-w-[80rem] px-5 py-10 md:px-9 md:py-14">
      <div className="mx-auto max-w-5xl">
      {/* Header */}
      <header className="mb-10 space-y-2">
        <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">Playlists</h1>
        <p className="text-sm text-muted-foreground">Música para descubrir, sentir y disfrutar.</p>
      </header>

      {/* Content */}
      {showSkeleton ? (
        <div className="space-y-10">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`sk-${i}`} className="animate-pulse space-y-3">
              <div className="h-4 w-32 rounded bg-muted" />
              <div className="h-6 w-2/3 rounded bg-muted" />
              <div className="h-[352px] w-full rounded-xl bg-muted" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
          No se pudieron cargar playlists.
        </p>
      ) : filteredList.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Aún no hay playlists publicadas.
        </p>
      ) : (
        <div className="space-y-12">
          {visiblePlaylists.map((post, index) => {
            const spotifyLink = post.spotify_url ? getSpotifyCanonicalUrl(post.spotify_url) : null;

            return (
              <article key={post.id} className="group">
                {/* Playlist info */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      {String(index + 1).padStart(2, "0")}
                    </p>
                    <Link to={`/post/playlist/${post.id}`}>
                      <h2 className="mt-1 font-display text-xl font-bold leading-tight tracking-tight text-foreground transition-colors hover:text-primary md:text-2xl">
                        {post.title}
                      </h2>
                    </Link>
                    {post.description ? (
                      <LinkifiedText text={post.description} className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground line-clamp-2" />
                    ) : null}
                  </div>

                  {spotifyLink ? (
                    <a
                      href={spotifyLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-3.5 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground transition-all hover:-translate-y-0.5 hover:border-primary hover:text-primary"
                    >
                      Abrir en Spotify ↗
                    </a>
                  ) : null}
                </div>

                {/* Always-visible embed */}
                {post.spotify_url ? (
                  <SpotifyEmbed
                    url={post.spotify_url}
                    height={SPOTIFY_PLAYLIST_EMBED_HEIGHT}
                    deferUntilVisible
                  />
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {/* Load more sentinel */}
      {hasMore && !showSkeleton && !isError ? (
        <div ref={loadMoreRef} className="py-10 text-center text-xs text-muted-foreground">
          Cargando más playlists…
        </div>
      ) : null}
      </div>
    </div>
  );
};

export default Playlists;
